import { VerticalBenchmarks } from './healthScoreBenchmarks'

export interface MetricInsightResult {
  metricKey: string
  title: string
  statement: string
  tierLine: string
  impactDollars: number
  varianceRatio: number
  isExceeding: boolean
}

function formatCurrency(amount: number): string {
  const rounded = Math.round(Math.abs(amount))
  return `$${rounded.toLocaleString('en-US')}`
}

export function computeMetricInsight({
  metricKey,
  actual,
  benchmark,
  totalSales,
  currentInventory,
  currentCogs,
  totalInventoryValue,
}: {
  metricKey: 'salesGrowth' | 'turn' | 'markdowns' | 'freshness' | 'grossMargin' | 'cashMargin'
  actual: number
  benchmark: number
  totalSales: number
  currentInventory: number
  currentCogs: number
  totalInventoryValue: number
}): MetricInsightResult {
  let isExceeding = false
  let impactDollars = 0
  let statement = ''
  let title = ''

  if (metricKey === 'markdowns') {
    // For markdowns, lower actual is better
    isExceeding = actual <= benchmark
  } else {
    isExceeding = actual >= benchmark
  }

  const varianceRatio = benchmark !== 0 ? Math.abs(actual - benchmark) / Math.abs(benchmark) : 0

  switch (metricKey) {
    case 'salesGrowth': {
      title = 'Sales Growth'
      if (isExceeding) {
        // [X] = Total Sales × (Actual Sales Growth % − Benchmark Sales Growth %)
        impactDollars = (totalSales * (actual - benchmark)) / 100
        statement = `At your current annual sales volume, outperforming your growth benchmark has generated approximately **${formatCurrency(impactDollars)}** in additional revenue this period compared to what benchmark performance alone would have delivered. Those additional funds can be used to further grow your business and generate more profit.`
      } else {
        // [X] = (Total Sales × (Benchmark Growth % − Actual Growth %)) / 12
        impactDollars = (totalSales * (benchmark - actual)) / 100 / 12
        statement = `Prioritize your top-selling classes: closely follow your OTB and make sure you are landing products on time and then actively promote once the product is received. Getting growth back to benchmark could recover **${formatCurrency(impactDollars)}** in monthly revenue.`
      }
      break
    }
    case 'turn': {
      title = 'Turn'
      if (isExceeding) {
        // [X] = (Total Sales / Benchmark Turn Rate) − (Total Sales / Actual Turn Rate)
        if (benchmark > 0 && actual > 0) {
          impactDollars = totalSales / benchmark - totalSales / actual
        }
        statement = `Turning inventory above benchmark means your cash is captured faster than expected. At your current inventory value, you've effectively freed up approximately **${formatCurrency(impactDollars)}** in working capital compared to a store with similar sales and turning merely at benchmark. This is a metric that many retailers struggle to improve, but you are ahead of the curve.`
      } else {
        // [X] = Current Inventory − (Current COGS / Benchmark Turn)
        if (benchmark > 0) {
          impactDollars = Math.max(0, currentInventory - currentCogs / benchmark)
        }
        statement = `Identify and exit low-velocity SKUs through markdowns, promotions, or bundling to accelerate sell-through on existing stock, then concentrate future inventory investment in faster-turning items. Increasing your turn can release **${formatCurrency(impactDollars)}** in cash that is currently tied up in excess inventory.`
      }
      break
    }
    case 'markdowns': {
      title = 'Markdowns'
      if (isExceeding) {
        // [X] = Total Sales × (Benchmark Markdown % − Actual Markdown %)
        impactDollars = (totalSales * (benchmark - actual)) / 100
        statement = `By keeping markdowns below benchmark, you've protected approximately **${formatCurrency(impactDollars)}** in gross margin this period that would otherwise have been given away in discounts. If you press ahead with sales growth, move inventory efficiently, and limit your markdowns, then you will have excellent retail health.`
      } else {
        // [X] = (Actual Markdown % − Benchmark Markdown %) × Total Sales
        impactDollars = ((actual - benchmark) / 100) * totalSales
        statement = `Your inventory is likely overbought in several classes. Tighten your OTB on those classes now, and set a sell-through trigger (e.g. under 40% by week 4 = take a shallow markdown early). Getting markdowns to benchmark recovers **${formatCurrency(impactDollars)}** in gross margin.`
      }
      break
    }
    case 'freshness': {
      title = 'Inventory Freshness'
      if (isExceeding) {
        // [X] = Total Inventory Value × (Actual Freshness % − Benchmark Freshness %)
        impactDollars = (totalInventoryValue * (actual - benchmark)) / 100
        statement = `Maintaining freshness above benchmark increases your OTB allowance by an estimated **${formatCurrency(impactDollars)}**, and keeps your OTB free to invest in products your customers actually want to buy right now. Your range of products is fresh and desirable — keep up the good work!`
      } else {
        // [X] = (Benchmark Freshness % − Current Freshness %) × Current Inventory
        impactDollars = ((benchmark - actual) / 100) * (currentInventory || totalInventoryValue)
        statement = `Aging inventory is blocking your OTB and setting up a scenario of declining sales due to lack of fresh sellable inventory. Move your oldest SKUs to a clearance position or off-price channel now to free up cash for fresh products. You can create **${formatCurrency(impactDollars)}** in additional open-to-buy to reinvest in fresh, faster-turning products.`
      }
      break
    }
    case 'grossMargin': {
      title = 'Gross Margin'
      if (isExceeding) {
        // [X] = Total Sales × (Actual Gross Margin % − Benchmark Gross Margin %)
        impactDollars = (totalSales * (actual - benchmark)) / 100
        statement = `At your current annual sales volume, you have generated roughly **${formatCurrency(impactDollars)}** in profit above what benchmark performance would have delivered this period. These gains are above what your peers would otherwise experience.`
      } else {
        // [X] = (Benchmark Gross Margin % − Actual Gross Margin %) × Total Sales
        impactDollars = ((benchmark - actual) / 100) * totalSales
        statement = `You may have an opportunity to raise retail pricing, where markets allow, on high-demand items to capture more margin per unit sold. The other option is negotiating deeper discounts with your vendors. At your current sales volume, you could gain **${formatCurrency(impactDollars)}** in gross margin dollars.`
      }
      break
    }
    case 'cashMargin': {
      title = 'Cash Margin'
      if (isExceeding) {
        // [X] = (Actual Cash Margin % − Benchmark Cash Margin %) × Total Sales
        impactDollars = ((actual - benchmark) / 100) * totalSales
        statement = `At your current sales volume, and at the rate you are exceeding the benchmark for cash margin %, you're keeping roughly **${formatCurrency(impactDollars)}** more than a peer-performing store. This results in the creation of additional cash that is available to reinvest in inventory, operations, or growth. You display a high level of efficiency — keep up the good work!`
      } else {
        // [X] = (Benchmark Cash Margin % − Actual Cash Margin %) × Total Sales
        impactDollars = ((benchmark - actual) / 100) * totalSales
        statement = `Your product mix may be working against your margin. Identify your top 3 classes by margin rate and shift floor space and OTB toward them in your next buy. At your current sales volume, the gap against benchmark represents **${formatCurrency(impactDollars)}** in unrealized cash margin.`
      }
      break
    }
  }

  return {
    metricKey,
    title,
    statement,
    tierLine: isExceeding ? 'Top tier — keep protecting it' : 'Opportunity tier — action required to recover margin',
    impactDollars: Math.max(0, impactDollars),
    varianceRatio,
    isExceeding,
  }
}
